import { useState, useEffect, useRef } from 'react';
import { 
  Key, Plus, Copy, AlertTriangle, CheckCircle, X, Trash2, Loader,
  Shield, Sparkles, ExternalLink, Code
} from 'lucide-react';
import { apiKeyService } from '../../services/PlatformService';
import { formatDateTime } from '../../lib/dateHelpers';

const ApiKeysManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [newKeyData, setNewKeyData] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [keyNameError, setKeyNameError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const successTimeoutRef = useRef(null);

  const showTemporarySuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccessMessage(false);
      successTimeoutRef.current = null;
    }, 3000);
  };

  const fetchApiKeys = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiKeyService.listApiKeys();
      setApiKeys(response.data || []);
    } catch (err) {
      console.error('Erro ao buscar chaves de API:', err);
      setError(err.response?.data?.detail || err.message || 'Falha ao carregar suas chaves de API.');
    } finally {
      setIsLoading(false);
    }
  };

  const createApiKey = async () => {
    setKeyNameError('');
    
    if (newKeyName.length > 50) {
      setKeyNameError('O nome da chave deve ter no máximo 50 caracteres');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const response = await apiKeyService.createApiKey(newKeyName || 'API Key');
      setNewKeyData(response.data);
      setKeyCopied(false);
      
      fetchApiKeys();
    } catch (err) {
      console.error('Erro ao criar chave de API:', err);
      setError(err.response?.data?.detail || err.message || 'Falha ao criar nova chave de API.');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteApiKey = async () => {
    if (!keyToDelete) return;
    
    setIsDeleting(true);
    
    try {
      await apiKeyService.deleteApiKey(keyToDelete.id);
      
      setShowDeleteModal(false);
      setKeyToDelete(null);
      showTemporarySuccessMessage('Chave de API deletada com sucesso');
      
      fetchApiKeys();
    } catch (err) {
      if (keyToDelete.key_preview.startsWith("system")) {
        alert("Essa chave não pode ser deletada.")
      }
      console.error('Erro ao deletar chave de API:', err);
      setError(err.response?.data?.detail || err.message || 'Falha ao deletar chave de API.');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyApiKey = (key) => {
    navigator.clipboard.writeText(key).then(
      () => {
        setKeyCopied(true);
        showTemporarySuccessMessage('Chave copiada para a área de transferência');
      },
      (err) => {
        console.error('Erro ao copiar:', err);
        setError('Erro ao copiar chave');
      }
    );
  };

  const handleNameChange = (e) => {
    setNewKeyName(e.target.value);
    if (keyNameError) setKeyNameError('');
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setNewKeyName('');
    setKeyNameError('');
    setNewKeyData(null);
    setKeyCopied(false);
  };

  const handleKeyCopiedClose = () => {
    if (keyCopied) {
      handleCancelCreate();
    } else {
      alert('Por favor, copie sua chave antes de fechar');
    }
  };

  function truncateText(text) {
  if (!text) return '';
  
  // Limitar a 50 caracteres
  const byChars = text.length > 50 ? text.slice(0, 50) + '...' : text;

  // Limitar a 4 palavras
  const words = byChars.split(' ');
  if (words.length > 4) {
    return words.slice(0, 4).join(' ') + '...';
  }

  return byChars;
}

  useEffect(() => {
    fetchApiKeys();
    
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
                <Key className="w-6 h-6 text-blue-600" />
              </div>
              Chaves de API
            </h2>
            <p className="mt-2 text-gray-600">
              Use chaves de API para integrar seus funcionários digitais em aplicações externas
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-5 py-3 bg-gradient-to-r from-green-800 to-green-600 rounded-2xl text-white font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nova Chave
          </button>
        </div>

        {/* Mensagens */}
        {showSuccessMessage && (
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 rounded-2xl p-4 flex items-center shadow-sm">
            <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
            <div className="flex-1 font-medium">{successMessage}</div>
            <button
              onClick={() => setShowSuccessMessage(false)}
              className="text-green-600 hover:text-green-800 p-1 rounded-lg hover:bg-green-100 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-800 rounded-2xl p-4 flex items-center shadow-sm">
            <AlertTriangle className="w-5 h-5 mr-3 text-red-600" />
            <div className="flex-1 font-medium">{error}</div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800 p-1 rounded-lg hover:bg-red-100 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-5 border border-indigo-100">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Segurança em Primeiro Lugar</h4>
                <p className="text-sm text-gray-600">
                  Suas chaves são criptografadas e nunca são expostas após a criação
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
            <div className="flex items-start gap-3">
              <Code className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Documentação Completa</h4>
                <a href="#" className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                  Ver guia de integração
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Chaves */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">Carregando chaves...</p>
            </div>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6">
              <Key className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Nenhuma chave de API ainda
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Crie sua primeira chave para começar a integrar seus funcionários digitais em outras aplicações
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-800 to-green-600 rounded-2xl text-white font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Criar Primeira Chave
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Nome da Chave
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Criada em
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Último uso
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Total de Usos
                  </th>
                  <th className="relative px-6 py-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
                          <Key className="w-4 h-4 text-blue-600" />
                        </div>
                        <span  className="text-sm font-semibold text-gray-900">
                          {truncateText(key.name)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${
                        key.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          key.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        {key.status === 'active' ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(key.created_at)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                      {key.last_used ? formatDateTime(key.last_used) : (
                        <span className="text-gray-400">Nunca usada</span>
                      )}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{key.usage_count || 0}</span>
                        <span className="text-xs text-gray-500">vezes</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-right">
                      {key.status === 'active' && (
                        <button
                          onClick={() => {
                            setKeyToDelete(key);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                          title="Deletar Chave"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Criar Chave */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
            {!newKeyData ? (
              <>
                <div className="flex justify-between items-center p-6 border-b">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-indigo-100 to-green-100 rounded-xl">
                      <Key className="w-5 h-5 text-green-600" />
                    </div>
                    Criar Nova Chave de API
                  </h3>
                  <button
                    onClick={handleCancelCreate}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="p-6">
                  <div className="mb-6">
                    <label htmlFor="key-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome da Chave
                    </label>
                    <input
                      id="key-name"
                      type="text"
                      className={`w-full px-4 py-3 text-lg border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 transition-all ${
                        keyNameError ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}
                      placeholder="Ex: Integração Website"
                      value={newKeyName}
                      onChange={handleNameChange}
                      maxLength={50}
                    />
                    {keyNameError && (
                      <p className="mt-2 text-sm text-red-600">{keyNameError}</p>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-amber-800 mb-1">Importante!</h4>
                        <p className="text-sm text-amber-700">
                          A chave será exibida apenas uma vez. Certifique-se de copiá-la e guardá-la em local seguro.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 rounded-b-3xl flex justify-end gap-3">
                  <button
                    onClick={handleCancelCreate}
                    className="px-5 py-2.5 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={createApiKey}
                    disabled={isCreating}
                    className="px-5 py-2.5 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {isCreating ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Gerando...
                      </>
                    ) : (
                      'Gerar Chave'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center p-6 border-b">
                  <h3 className="text-xl font-bold text-gray-900">
                    Chave de API Criada!
                  </h3>
                  <button
                    onClick={handleKeyCopiedClose}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="p-6">
                  <div className="mb-6 text-center">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl flex items-center justify-center mb-4">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Chave gerada com sucesso!
                    </h4>
                    <p className="text-gray-600">
                      Copie sua chave agora. Ela não será exibida novamente.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sua Chave de API
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl font-mono text-sm"
                        value={newKeyData.key}
                        readOnly
                      />
                      <button
                        onClick={() => copyApiKey(newKeyData.key)}
                        className={`px-4 py-3 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
                          keyCopied 
                            ? 'bg-green-600 text-white' 
                            : 'bg-gradient-to-r from-green-800 to-green-600 text-white hover:from-green-700 hover:to-green-700'
                        }`}
                      >
                        {keyCopied ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 rounded-b-3xl flex justify-end">
                  <button
                    onClick={handleKeyCopiedClose}
                    disabled={!keyCopied}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
                      keyCopied 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {keyCopied ? 'Concluído' : 'Copie a chave primeiro'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Deletar */}
      {showDeleteModal && keyToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-100 to-pink-100 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Deletar Chave de API
                </h3>
                <p className="text-gray-600">
                  Esta ação é irreversível
                </p>
              </div>
            </div>
            
            <p className="mb-6 text-gray-700">
              Tem certeza que deseja deletar a chave <strong className="text-gray-900">{keyToDelete.name}</strong>?
            </p>
            
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4 mb-6">
              <p className="text-sm text-red-700">
                <strong>Atenção:</strong> Todas as integrações usando esta chave pararão de funcionar imediatamente.
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setKeyToDelete(null);
                }}
                className="px-5 py-2.5 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={deleteApiKey}
                disabled={isDeleting}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-green-600 text-white rounded-xl font-semibold hover:from-red-700 hover:to-green-700 disabled:opacity-50 inline-flex items-center transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isDeleting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Deletando...
                  </>
                ) : (
                  'Deletar Chave'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysManager;